import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'src/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import {
    AccessoriesPageData,
    AccessoriesPageVariables,
    getAccessoriesPageConfig,
} from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Query } from 'src/modules/common/components/Query/Query';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { ValvesBreadcrumbValue } from 'src/modules/common/components/ValvesBreadcrumbValue/ValvesBreadcrumbValue';
import { ValvesTooltipValue } from 'src/modules/common/components/ValvesTooltipValue/ValvesTooltipValue';
import { LocationState } from 'src/modules/LocationState.ts';
import { initLocationState } from 'src/modules/locationStateContext.ts';
import { GetQty, IsRecommended, parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { ValveFilterTooltip } from 'src/modules/common/components/ValveFilterTooltip/ValveFilterTooltip.tsx';

export const AccessoriesPage = () => {
    const {
        valveLabel,
        actuatorLabel,
        page,
    } = useParams();

    if (!valveLabel) {
        throw new Error('No Valve selected');
    }
    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }
    if (!page) {
        throw new Error('No page provided');
    }
    const { isMobile } = useContext(RuntimeContext);
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }
    const { state: userInputState, locationState, setLocationState } = useUserInputState([]);

    useEffect(() => {
        if (locationState.valveLabel !== valveLabel) {
            setLocationState({ ...locationState, valveLabel });
        }
        if (locationState.actuatorLabel !== actuatorLabel) {
            setLocationState({ ...locationState, actuatorLabel });
        }
        if (valveLabel === actuatorLabel && locationState.valvesPage !== page) {
            setLocationState({ ...locationState, valvesPage: page });
        }
    }, [locationState, setLocationState, valveLabel, actuatorLabel, page]);

    const onChange = useCallback((label: string, getQty: GetQty) => {
        let selectedAccessories: LocationState['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({
            ...locationState,
            selectedAccessories,
        });
    }, [locationState, setLocationState]);

    const [
        searchParams,
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...pageConfig.actuatorsFilterConfig,
    ]);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const navigate = useNavigate();

    useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: [],
    });

    const breadcrumbsProps: Breadcrumbs = [
        {
            title: MESSAGES.selectValveType,
            value: homePageValue,
            tooltipValue: <ValveFilterTooltip value={homePageValue}/>,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.RequestedFlow,
            value: <ValvesBreadcrumbValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            tooltipValue: <ValvesTooltipValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.UserInput,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedValve,
            value: locationState.valveLabel,
            state: locationState,
            to: {
                pathname: `${PATHS.Valves}/${locationState.valvesPage}`,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedActuator,
            value: locationState.actuatorLabel,
            state: locationState,
            to: {
                pathname: [
                    ...(locationState.valveLabel === locationState.actuatorLabel ? [
                        PATHS.Valves,
                        locationState.valvesPage,
                    ] : [
                        PATHS.Actuators,
                        encodeURIComponent(locationState.valveLabel),
                        page,
                    ]),
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            selected: true,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            actionShown: locationState.selectedAccessories.length > 0,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    selectedAccessories: [],
                });
            },
            to: '#',
        },
    ];

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: [
                        ...(locationState.valveLabel === locationState.actuatorLabel ? [
                            PATHS.Valves,
                            locationState.valvesPage,
                        ] : [
                            PATHS.Actuators,
                            encodeURIComponent(locationState.valveLabel),
                            page,
                        ]),
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <NavigationButton
                type="forward"
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 5 - add to schedule',
                    eventLabel: locationState.selectedAccessories.length > 0
                        ? 'add combo and accessories to schedule' : 'add to schedule',
                }}
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Checkout,
                        encodeURIComponent(valveLabel),
                        encodeURIComponent(actuatorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const variables = useMemo((): {
        variables: AccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                actuatorLabel,
                valveLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        valveLabel,
        userSettings,
    ]);

    const gatewaySource = '_6976';
    const toolsSource = '_6311';
    const isRegularAccessory = (
        { source }: AccessoriesPageData['vssAccessories']['valveAccessories'][0],
    ) => ![gatewaySource, toolsSource].includes(source);
    const isGatewayAccessory = (
        { source }: AccessoriesPageData['vssAccessories']['valveAccessories'][0],
    ) => gatewaySource === source;
    const isToolsAccessory = (
        { source }: AccessoriesPageData['vssAccessories']['valveAccessories'][0],
    ) => toolsSource === source;

    const renderAccessory = (
        item: AccessoriesPageData['vssAccessories']['valveAccessories'][0]['item'],
        isRecommended: IsRecommended,
        getQty: GetQty,
    ) => (
        <Accessory
            chip={isRecommended(item.Label) ? <I18n messageId={MESSAGES.recommended}/> : null}
            accessory={item}
            onSelect={() => onChange(item.Label, getQty)}
            selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
            key={`${item.Label}-${item.ID}`}
        />
    );

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={<BreadcrumbContainer isMobile={isMobile} breadcrumbs={breadcrumbsProps}/>}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<AccessoriesPageData, AccessoriesPageVariables>
                        query={pageConfig.query}
                        variables={variables}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.vssAccessories.valveAccessories.length === 0 &&
                            (data.vssAccessories.actuatorAccessories?.length || 0) === 0) {
                                return <NoData/>;
                            }

                            const {
                                isRecommended,
                                getQty,
                            } = parseRecommended(data.vssAccessories.valve.connectors);

                            return (
                                <div className="vss-accessories-page">
                                    {data.vssAccessories.valveAccessories &&
                                    data.vssAccessories.valveAccessories.filter(isRegularAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageValvesTitle}>
                                            {data.vssAccessories.valveAccessories.filter(isRegularAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}

                                    {data.vssAccessories.valveAccessories &&
                                    data.vssAccessories.valveAccessories.filter(isGatewayAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageGateways}>
                                            {data.vssAccessories.valveAccessories.filter(isGatewayAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}

                                    {data.vssAccessories.valveAccessories &&
                                    data.vssAccessories.valveAccessories.filter(isToolsAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageTools}>
                                            {data.vssAccessories.valveAccessories.filter(isToolsAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}

                                    {valveLabel !== actuatorLabel &&
                                    data.vssAccessories.actuatorAccessories &&
                                    data.vssAccessories.actuatorAccessories.filter(isRegularAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageActuatorTitle}>
                                            {data.vssAccessories.actuatorAccessories.filter(isRegularAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}

                                    {valveLabel !== actuatorLabel &&
                                    data.vssAccessories.actuatorAccessories &&
                                    data.vssAccessories.actuatorAccessories.filter(isGatewayAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageGateways}>
                                            {data.vssAccessories.actuatorAccessories.filter(isGatewayAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}

                                    {valveLabel !== actuatorLabel &&
                                    data.vssAccessories.actuatorAccessories &&
                                    data.vssAccessories.actuatorAccessories.filter(isToolsAccessory).length > 0 && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageTools}>
                                            {data.vssAccessories.actuatorAccessories.filter(isToolsAccessory)
                                                .sort(sortAccessoriesBy(isRecommended))
                                                .map(
                                                    ({ item }) => renderAccessory(
                                                        item,
                                                        isRecommended,
                                                        getQty,
                                                    ),
                                                )}
                                        </AccessoryGrid>
                                    )}
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
